import { NgModule } from '@angular/core';
import { Routes, RouterModule } from '@angular/router';
import { PublicLayoutComponent } from './main/layout/public/public-layout.component';
import { PrivateLayoutComponent } from './main/layout/private/private-layout.component';
import { AuthGuard } from './modules/auth/guard/auth.guard';
import { CorePreloadingStrategy } from '@app/core/service/core-preloader';
import { NotFoundPageComponent } from '@app/main/page/not-found/not-found.component';
import { PublicPatientInfoPageComponent } from '@app/main/page/public-patient-info/public-patient-info.component';
import { PublicMolstLookupComponent } from '@app/main/page/public-molst-lookup/public-molst-lookup.component';
import { PublicDirectiveVerifyComponent } from '@app/main/page/public-directive-verify/public-directive-verify.component';
import { DeviceEnrollComponent } from '@app/main/page/device-enroll/device-enroll.component';

const routes: Routes = [
  {
    path: '',
    children: [
      /**
       * @todo - delete when appears public layout
       */
      { path: '', pathMatch: 'full', redirectTo: 'auth' },
      {
        path: '',
        component: PublicLayoutComponent,
        children: [
          {
            path: 'public-patient-info/:id',
            component: PublicPatientInfoPageComponent,
          },
          {
            path: 'public-molst-lookup',
            component: PublicMolstLookupComponent,
          },
          {
            // Secure, blockchain-verified lookup by opaque token (preferred).
            path: 'public-molst-lookup/verify/:qrToken',
            component: PublicMolstLookupComponent,
          },
          {
            // Legacy break-glass lookup by integer id (already-printed wristbands).
            path: 'public-molst-lookup/:molstId',
            component: PublicMolstLookupComponent,
          },
          {
            // VA advance directive break-glass verify (printed QR lands here).
            path: 'directive-verify/:qrToken',
            component: PublicDirectiveVerifyComponent,
          },
          {
            // One-time device-enrollment link (tiered disclosure): open ON the
            // device to register it for full-document break-glass access.
            path: 'device-enroll/:code',
            component: DeviceEnrollComponent,
          },
        ],
      },
      {
        path: 'auth',
        loadChildren: () =>
          import('@app/modules/auth').then((m) => m.AuthModule),
      },
      {
        // Epic SMART-on-FHIR clinician login + live patient search (demo).
        path: 'epic',
        loadChildren: () => import('@app/modules/epic').then((m) => m.EpicModule),
      },
    ],
  },
  {
    /**
     * Witness mode. Deliberately NOT under PrivateLayoutComponent: the person
     * holding the device is not the account holder, so the page renders with
     * no header, no sidebar, no menu and no links. Entry is gated on a
     * handover the declarant started, and the only way out is their PIN.
     */
    path: 'witness',
    canActivate: [AuthGuard],
    loadChildren: () =>
      import('@app/modules/witness-mode').then((m) => m.WitnessModeModule),
  },
  {
    path: 'office',
    component: PrivateLayoutComponent,
    canActivate: [AuthGuard],
    canActivateChild: [AuthGuard],
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        data: { preload: false },
        loadChildren: () =>
          import('@app/modules/dashboard').then((m) => m.DashboardModule),
        // loadChildren: () =>
        //   import('@app/modules/patient-list').then((m) => m.PatientListModule),
      },
      {
        // My profile as a real page. The Account drawer still exists for a
        // quick password change; this is where a first-time user is sent.
        path: 'profile',
        data: { preload: false },
        loadChildren: () => import('@app/modules/profile').then((m) => m.ProfileModule),
      },
      {
        // Patient portal: action-center dashboard + care team / documents / help.
        path: 'portal',
        data: { preload: false },
        loadChildren: () =>
          import('@app/modules/patient-portal').then((m) => m.PatientPortalModule),
      },
      {
        // "My Advance Care Plan" — the guided ACP questionnaire wizard.
        path: 'acp',
        data: { preload: false },
        loadChildren: () => import('@app/modules/acp').then((m) => m.AcpModule),
      },
      {
        path: 'invitations',
        data: { preload: false },
        loadChildren: () =>
          import('@app/modules/invitations').then((m) => m.InvitationsModule),
      },
      {
        path: 'living-will',
        data: { preload: false },
        loadChildren: () =>
          import('@app/modules/living-will').then((m) => m.LivingWillModule),
      },
      {
        path: 'patients',
        data: { preload: false },
        loadChildren: () =>
          import('@app/modules/patient-list').then((m) => m.PatientListModule),
      },
      {
        // Clinician: patients who opened their record to me (consent / invitation).
        path: 'shared',
        data: { preload: false },
        loadChildren: () =>
          import('@app/modules/clinician-shared').then((m) => m.ClinicianSharedModule),
      },
      {
        // Clinician: my patients' medical orders, plus any I signed.
        path: 'orders',
        data: { preload: false },
        loadChildren: () => import('@app/modules/org-orders').then((m) => m.OrgOrdersModule),
      },
      {
        path: 'molst-form',
        data: { preload: false },
        loadChildren: () =>
          import('@app/modules/molst-form').then((m) => m.MolstFormModule),
      },
      {
        // Consent: sign, anchor on blockchain, share via QR, track & verify.
        path: 'consent',
        data: { preload: false },
        loadChildren: () => import('@app/modules/consent').then((m) => m.ConsentModule),
      },
      {
        // Account security: two-factor (TOTP) enrollment.
        path: 'security',
        data: { preload: false },
        loadChildren: () => import('@app/modules/security').then((m) => m.SecurityModule),
      },
      {
        // Admin: account approval / suspension (login access control).
        path: 'admin',
        data: { preload: false },
        loadChildren: () => import('@app/modules/admin').then((m) => m.AdminModule),
      },
      // The 'clinical' routes (consent-gated trials / MRI / reports) were
      // retired 2026-08-04 — general record exchange is the EHR's job. The
      // backend answers 410 on the old API routes; the old URLs fall through
      // to the not-found page.
    ],
  },
  { path: '**', component: NotFoundPageComponent },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
    preloadingStrategy: CorePreloadingStrategy
}),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
